/** @format */

import React, { FC, ReactElement, useEffect, useState } from 'react';
import { SimCardServiceTypeItem } from '../../../../interfaces/collections/SimCardServiceInterface';
import CardHeader from '../../../../components/Heading/CardHeaderExt';
import Card from '../../../../components/Card/Card';
import CardBody from '../../../../components/Card/CardBody';
import { Build } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { ApiCollectionInterface } from '../../../../interfaces/collections/ApiCollectionInterface';
import { ItemLoaders } from '../../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../../helpers/FormGeneral';
import { OperatorMvneTypeMap, ServicesMvneType } from '../../../../modules/lib/hooks/useOperatorType';
import { SimpleSelect, SimpleSelectElements } from '../../../../components/Adescom/SimpleSelect';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import ActivateFeatureButton from '../../../../components/Buttons/ActivateFeatureButton';
import i18n from '../../../../core/i18n';
import {
    SimCardServiceTypeMappedComponentInterface,
    SimCardServiceTypeMappedComponentPost,
} from '../../../../interfaces/platform/SimCardServiceTypeMappedComponentInterface';
import { SimCardServiceTypeMappedCustomForm } from './SimCardServiceTypeMappedCustomForm';
import { Auth } from '../../../../modules/auth/Auth';
import { TableBody, TableHead, TableRow } from '@material-ui/core';
import TableCell from '@material-ui/core/TableCell';
import I18n from '../../../../components/Translate/I18n';
import TableHeaderContentCell from '../../../../components/Table/TableHeaderContentCell';
import withStyles from '@material-ui/core/styles/withStyles';
import OplAppStyle from '../../../../assets/themes/OPL/jss/App/OplAppStyle';
import Table from '@material-ui/core/Table';
import DeleteButton from '../../../../components/Buttons/DeleteButton';
import EditButton from '../../../../components/Buttons/EditButton';

type SimCardServiceTypeMappedCustomParamsType = {
    simCardServiceType: SimCardServiceTypeItem;
    classes: any;
};

const SimCardServiceTypeMappedCustomParams: FC<SimCardServiceTypeMappedCustomParamsType> = ({ simCardServiceType, classes }): ReactElement => {
    const dispatch = useDispatch();

    const [id, setId] = useState(simCardServiceType.id);
    const [bmeComponents, setBmeComponent] = useState<Array<SimpleSelectElements> | null>(null);
    const [selected, setSelected] = useState(-1);
    const [addOpen, setAddOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [mappedElements, setMappedElements] = useState<ApiCollectionInterface<SimCardServiceTypeMappedComponentInterface> | null>(null);
    const [params, setParams] = useState('{}');
    const [element, setElement] = useState<SimCardServiceTypeMappedComponentInterface | null>(null);

    useEffect(() => {
        if (id !== simCardServiceType.id) {
            setId(simCardServiceType.id);
            setMappedElements(null);
            setBmeComponent(null);
        }
    }, [simCardServiceType, id]);

    useEffect(() => {
        if (mappedElements === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_mapped_components')),
                'no-dispatch',
                [
                    { name: 'primarySimCardServiceType', value: simCardServiceType.id },
                    { name: 'deleted', value: false },
                ],
                [{ name: 'createdAt', value: 'asc' }],
                (data: ApiCollectionInterface<SimCardServiceTypeMappedComponentInterface>): void => {
                    setMappedElements(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [mappedElements, simCardServiceType, dispatch]);

    useEffect(() => {
        if (bmeComponents === null && mappedElements !== null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_types')),
                'no-dispatch',
                [
                    { name: 'systemComponent', value: false },
                    { name: 'available', value: true },
                    { name: 'serviceType[]', value: ServicesMvneType.BME },
                    { name: 'operatorType', value: OperatorMvneTypeMap.OPL },
                    { name: 'pagination', value: false },
                ],
                [{ name: 'description', value: 'asc' }],
                (data: ApiCollectionInterface<SimCardServiceTypeItem>): void => {
                    const list: Array<SimpleSelectElements> = [];
                    data['hydra:member'].map((item): void => {
                        list.push({ value: item.id, name: item.description + ' (' + item.mainname + ')' ?? 'no-name' });
                    });
                    setBmeComponent(list);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [bmeComponents, simCardServiceType, dispatch, mappedElements]);

    const chooseNew = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = event.target.value ?? '-1';
        setSelected(parseInt(selected));
    };

    const newParams = (params: string): void => {
        const obj = {} as SimCardServiceTypeMappedComponentPost;
        obj.params = params;
        obj.primarySimCardServiceType = '/base-api/api/platform/sim_card_service_types/' + simCardServiceType.id;
        obj.secondarySimCardServiceType = '/base-api/api/platform/sim_card_service_types/' + selected;
        obj.platformReseller = '/base-api/api/platform/platform_resellers/' + Auth.getUserPlatformResellerId();
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_mapped_components')),
            'no-dispatch',
            obj,
            (data: SimCardServiceTypeMappedComponentInterface): void => {
                setMappedElements(null);
                setAddOpen(false);
                setIsEdit(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const getServiceSecondaryName = (item: SimCardServiceTypeMappedComponentInterface): string => {
        if (bmeComponents) {
            for (let i = 0; i < bmeComponents.length; i++) {
                if ('/adescom-api/api/platform/sim_card_service_types/' + bmeComponents[i].value === item.secondarySimCardServiceType) {
                    const params = JSON.parse(item.params ?? '{}');
                    return bmeComponents[i].name + ' / (' + params.amount + ')';
                }
            }
        }
        return 'no defined';
    };

    const updateParams = (params: string): void => {
        if (element) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.putWithCallback(
                FormGeneralHelper.getByUri(element['@id']),
                'no-dispatch',
                {
                    ...element,
                    params: params,
                    platformReseller: FormGeneralHelper.changeAdescomApiIriToBaseApiIri(element.platformReseller),
                    primarySimCardServiceType: FormGeneralHelper.changeAdescomApiIriToBaseApiIri(element.primarySimCardServiceType),
                    secondarySimCardServiceType: FormGeneralHelper.changeAdescomApiIriToBaseApiIri(element.secondarySimCardServiceType),
                },
                (data: SimCardServiceTypeMappedComponentInterface): void => {
                    setElement(null);
                    setMappedElements(null);
                    setAddOpen(false);
                    setIsEdit(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    };

    const deleteParams = (item: SimCardServiceTypeMappedComponentInterface): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.deleteWithCallback(
            FormGeneralHelper.getByUri(item['@id']),
            'no-dispatch',
            (data: any): void => {
                setElement(null);
                setMappedElements(null);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };
    const tableRowRoot = { root: classes.tableRowRoot };

    return (
        <Card>
            <CardHeader icon={<Build />} trans="prov:feature.mapped_elements" />
            <CardBody>
                <GridContainer>
                    <GridItem xs={10}>
                        <SimpleSelect
                            label={'prov:feature.choose_service'}
                            required={false}
                            selectedValue={selected}
                            elements={bmeComponents ?? []}
                            dispatchName={'no-dispatch'}
                            callback={chooseNew}
                        />
                    </GridItem>
                    <GridItem xs={2} style={{ textAlign: 'center', marginTop: '15px' }}>
                        {selected !== -1 && (
                            <ActivateFeatureButton
                                callback={(): void => {
                                    setAddOpen(true);
                                    setIsEdit(false);
                                    setParams('{}');
                                }}
                                callbackParam={{}}
                                nonTableButton={true}
                                tooltipTitle={i18n.t('prov:feature.mapped_element_add')}
                                size="sm"
                            />
                        )}
                    </GridItem>
                </GridContainer>
                <GridContainer>
                    <GridItem xs={12} md={12} lg={12}>
                        <Table>
                            <TableHead className={classes.tableRoot}>
                                <TableRow>
                                    <TableCell classes={tableControlStickyHeader} padding="none" align="left" style={{ width: '10%' }}>
                                        <I18n trans="general:common.commands" />
                                    </TableCell>
                                    <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: '20%' }}>
                                        <TableHeaderContentCell
                                            title="prov:feature.mapped_component"
                                            textAlign="center"
                                            fieldName="verb"
                                            showSort={false}
                                            onlyFilter={false}
                                            defaultDirection="desc"
                                            searchFieldType="string"
                                            filters={[]}
                                            sort={[]}
                                            updateOrderList={[]}
                                            updateFilterList={[]}
                                            searchActionCallback={[]}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bmeComponents &&
                                    mappedElements &&
                                    mappedElements['hydra:member'].map((item) => {
                                        return (
                                            <TableRow classes={tableRowRoot} key={'key' + item.id} hover style={{ backgroundColor: 'white' }}>
                                                <TableCell classes={tableControlStickyHeader} padding="none" align="left">
                                                    <EditButton
                                                        callback={(): void => {
                                                            setParams(item.params);
                                                            setIsEdit(true);
                                                            setElement(item);
                                                            setAddOpen(true);
                                                        }}
                                                        callbackParam={{}}
                                                        tooltipTitle="general:common.edit"
                                                    />
                                                    <DeleteButton
                                                        callback={deleteParams}
                                                        callbackParam={{ ...item }}
                                                        tooltipTitle="general:common.delete"
                                                    />
                                                </TableCell>
                                                <TableCell classes={tableCellRoot} padding="none" align="left">
                                                    {getServiceSecondaryName(item)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </GridItem>
                </GridContainer>
                <SimCardServiceTypeMappedCustomForm
                    open={addOpen}
                    params={params}
                    updateParams={isEdit ? updateParams : newParams}
                    close={(): void => setAddOpen(false)}
                    isEdit={isEdit}
                />
            </CardBody>
        </Card>
    );
};

export default withStyles(OplAppStyle)(SimCardServiceTypeMappedCustomParams);
